import { useState, useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { api } from "@/lib/apiClient";

// SaaS re-platform: buyer/seller (marketplace counterparties) retired from the
// product surface. internal_admin/internal_reviewer/ceo remain until the
// control-plane + CEO transform land in Phase 1, then collapse to
// owner|admin|member|viewer (+ platform_admin/platform_support).
export type UserRole =
  | 'internal_admin'
  | 'internal_reviewer'
  | 'admin'
  | 'member'
  | 'ceo'
  | 'platform_admin'
  | 'platform_support'
  | null;

interface UseUserRoleResult {
  roles: UserRole[];
  primaryRole: UserRole;
  loading: boolean;
  hasRole: (role: UserRole) => boolean;
  isInternal: boolean;
  isAdmin: boolean;
  isCeo: boolean;
  /** Provenance platform staff (control plane) — read or write. */
  isPlatform: boolean;
  /** Platform staff with write access (suspend/reactivate, plan edits). */
  isPlatformAdmin: boolean;
}

const ROLES_FETCH_TIMEOUT_MS = 6000;

// Single-flight cache: many components call useUserRole, but roles only need one
// authoritative fetch per user per session — share the promise so we don't fan out
// N identical requests. A failed/timed-out fetch resolves to null and is NOT
// cached (evicted below), so a later mount retries instead of the whole session
// being poisoned with an empty result.
const rolesFetchCache = new Map<string, Promise<string[] | null>>();
function fetchRolesOnce(userId: string): Promise<string[] | null> {
  let p = rolesFetchCache.get(userId);
  if (!p) {
    const req = api.get<string[]>(`/users/${userId}/roles`);
    // Bound the wait so a hung roles call can't lock the app behind a spinner.
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), ROLES_FETCH_TIMEOUT_MS));
    p = Promise.race([req, timeout])
      .then((r) => {
        if (r === null) { rolesFetchCache.delete(userId); return null; } // timed out — allow retry
        return r;
      })
      .catch(() => { rolesFetchCache.delete(userId); return null; }); // failed — don't poison; allow retry
    rolesFetchCache.set(userId, p);
  }
  return p;
}

export const useUserRole = (): UseUserRoleResult => {
  const { user, isLoaded } = useUser();
  const [apiRoles, setApiRoles] = useState<UserRole[] | null>(null);
  const [rolesResolved, setRolesResolved] = useState(false);

  // Roles from Clerk publicMetadata (synced from the DB on the backend).
  const clerkRoles: UserRole[] = isLoaded && user?.publicMetadata?.roles
    ? (user.publicMetadata.roles as UserRole[])
    : [];

  const userId = user?.id;

  // Refresh roles from the API once after load — not only when Clerk metadata is
  // empty. Any authenticated call runs the backend platform-admin bootstrap, so
  // this both promotes a configured admin AND surfaces the result even for a user
  // whose existing metadata carries non-platform roles.
  useEffect(() => {
    if (!isLoaded || !userId) return;
    let cancelled = false;
    fetchRolesOnce(userId).then((r) => {
      if (cancelled) return;
      setApiRoles(r as UserRole[] | null);
      setRolesResolved(true);
    });
    return () => { cancelled = true; };
  }, [isLoaded, userId]);

  // The API (user_roles, synced to Clerk metadata) is authoritative once it
  // resolves: this both promotes a just-bootstrapped admin AND drops a revoked
  // role. On a failed/timed-out fetch (apiRoles === null) we fall back to Clerk
  // metadata, so a transient blip never strips a user's roles.
  const roles: UserRole[] = apiRoles ?? clerkRoles;

  // Stay "loading" until the one-time refresh resolves (success OR failure), so
  // gates/routing act on the resolved role set rather than possibly-stale metadata.
  const loading = !isLoaded || (!!userId && !rolesResolved);

  const hasRole = (role: UserRole): boolean => {
    if (!role) return false;
    return roles.includes(role);
  };

  const isInternal = roles.includes('internal_admin') || roles.includes('internal_reviewer');
  const isAdmin = roles.includes('internal_admin') || roles.includes('admin');
  const isCeo = roles.includes('ceo');
  const isPlatformAdmin = roles.includes('platform_admin');
  const isPlatform = isPlatformAdmin || roles.includes('platform_support');

  const primaryRole: UserRole =
    roles.includes('platform_admin') ? 'platform_admin' :
    roles.includes('platform_support') ? 'platform_support' :
    roles.includes('internal_admin') ? 'internal_admin' :
    roles.includes('internal_reviewer') ? 'internal_reviewer' :
    roles.includes('ceo') ? 'ceo' :
    roles[0] || null;

  return {
    roles,
    primaryRole,
    loading,
    hasRole,
    isInternal,
    isAdmin,
    isCeo,
    isPlatform,
    isPlatformAdmin,
  };
};
